import type { Locale } from "../api/types";
import { chromeLocale } from "./locale-context";

/** Every displayed time is Bangkok time, whatever the device's zone (agreed with stream W2). */
export const DISPLAY_TIME_ZONE = "Asia/Bangkok";

/**
 * UX-01 §4.6a (Revision 3.5), canonical for UI, email and exports:
 * EN "Wed 16 Sep 15:49" · TH "พ. 16 ก.ย. 15:49" · date only "Wed 16 Sep" · time only "15:49" ·
 * window "Wed 16 Sep 22:00–23:30", across midnight "Wed 16 Sep 23:00 – Thu 17 Sep 01:00" ·
 * another year "Tue 5 Jan 2027 09:10".
 * Weekday always; no leading zero on the day; 24-hour; Gregorian year only outside the current (Bangkok) year; no zone
 * suffix. Built from fixed arrays, never Intl short names (en-GB renders "Sept"). Machine-readable values stay ISO 8601.
 */
const WEEKDAYS: Record<Locale, readonly string[]> = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  th: ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."],
};
const MONTHS: Record<Locale, readonly string[]> = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  th: ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."],
};

/** Asia/Bangkok has been UTC+07:00 with no daylight saving since 1920, so wall-clock time is a fixed offset. */
const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

export interface BangkokParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
  /** 0 = Sunday */
  weekday: number;
  hour: number;
  minute: number;
}

type DateInput = string | number | Date | null | undefined;

function toInstant(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The Bangkok wall-clock parts of an instant, or null when it is not a valid date. */
export function bangkokParts(value: DateInput): BangkokParts | null {
  const instant = toInstant(value);
  if (!instant) return null;
  const wall = new Date(instant.getTime() + BANGKOK_OFFSET_MS);
  return {
    year: wall.getUTCFullYear(),
    month: wall.getUTCMonth() + 1,
    day: wall.getUTCDate(),
    weekday: wall.getUTCDay(),
    hour: wall.getUTCHours(),
    minute: wall.getUTCMinutes(),
  };
}

/** A calendar date without a zone (YYYY-MM-DD), read as that day itself rather than an instant. */
function calendarParts(text: string): BangkokParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (utc.getUTCMonth() !== month - 1) return null;
  return { year, month, day, weekday: utc.getUTCDay(), hour: 0, minute: 0 };
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Displayed dates are chrome: English for every reader, whatever the stored locale (ruling 2026-10-04, "no thai
 * language", extending R10's item-page rule to boards, lists and reports). The Thai names above stay for a later decision
 * and are reachable only through `chromeLocale()`.
 */
function lang(locale: Locale | undefined): Locale {
  return chromeLocale(locale) === "th" ? "th" : "en";
}

function dayText(p: BangkokParts, locale: Locale | undefined, now: Date): string {
  const l = lang(locale);
  const currentYear = bangkokParts(now)?.year;
  return `${WEEKDAYS[l][p.weekday]} ${p.day} ${MONTHS[l][p.month - 1]}${p.year === currentYear ? "" : ` ${p.year}`}`;
}

function timeText(p: BangkokParts): string {
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** Date only: "Wed 16 Sep" (TH "พ. 16 ก.ย."). A YYYY-MM-DD value is that calendar day; an instant is its Bangkok day. */
export function formatDate(value: DateInput, locale?: Locale, now: Date = new Date()): string {
  const p = typeof value === "string" && value.length === 10 ? calendarParts(value) : bangkokParts(value);
  return p ? dayText(p, locale, now) : "—";
}

/** Date and time: "Wed 16 Sep 15:49" (TH "พ. 16 ก.ย. 15:49"). */
export function formatDateTime(value: DateInput, locale?: Locale, now: Date = new Date()): string {
  const p = bangkokParts(value);
  return p ? `${dayText(p, locale, now)} ${timeText(p)}` : "—";
}

/** Time only, for a same-day context such as "Paused since": "15:49". */
export function formatTime(value: DateInput): string {
  const p = bangkokParts(value);
  return p ? timeText(p) : "—";
}

/**
 * A window: "Wed 16 Sep 22:00–23:30" on one Bangkok day, "Wed 16 Sep 23:00 – Thu 17 Sep 01:00" across midnight.
 * Without an end (e.g. a running attempt) it is "from Wed 16 Sep 22:00" / "ตั้งแต่ พ. 16 ก.ย. 22:00"; without a start, "—".
 */
export function formatWindow(start: DateInput, end: DateInput, locale?: Locale, now: Date = new Date()): string {
  const s = bangkokParts(start);
  if (!s) return "—";
  const e = bangkokParts(end);
  if (!e) return `${lang(locale) === "th" ? "ตั้งแต่" : "from"} ${formatDateTime(start, locale, now)}`;
  const sameDay = s.year === e.year && s.month === e.month && s.day === e.day;
  return sameDay ? `${dayText(s, locale, now)} ${timeText(s)}–${timeText(e)}` : `${formatDateTime(start, locale, now)} – ${formatDateTime(end, locale, now)}`;
}

/** "3h ago" style, for "Updated" columns. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const minutes = Math.round((now.getTime() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Man-days with two decimals. A missing estimate is never shown as 0 (UX-01 S4). */
export function formatMd(value: number | null | undefined): string {
  return typeof value === "number" ? `${value.toFixed(2)} MD` : "? MD";
}

/** Whole Bangkok calendar days a YYYY-MM-DD date has passed; 0 when not yet due. */
export function daysOverdue(dateText: string | null | undefined, now: Date = new Date()): number {
  if (!dateText) return 0;
  const due = new Date(`${dateText.slice(0, 10)}T23:59:59+07:00`).getTime();
  if (Number.isNaN(due) || now.getTime() <= due) return 0;
  return Math.ceil((now.getTime() - due) / 86_400_000);
}
